import { useEffect, useState } from "react";
import {
  CheckCircle,
  XCircle,
  Clock,
  Search,
  ChevronDown,
  Loader,
  FileText,
  User,
  Mail,
  Phone,
  Linkedin,
  Calendar,
  Award,
  Star,
  Eye,
  X,
  Users,
} from "lucide-react";
import { applicationApi, Application, showSuccess, showError } from "../lib";

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  under_review: "bg-blue-100 text-blue-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  shortlisted: "bg-purple-100 text-purple-800",
};

const AdminApplicationsPage = () => {
  const [applications, setApplications] = useState<Application[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });

  useEffect(() => {
    fetchApplications();
  }, [currentPage, statusFilter, typeFilter]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const result = await applicationApi.getAllApplications({
        page: currentPage,
        limit: 10,
        applicationType: typeFilter as "instructor" | "mentor" | undefined,
        status: statusFilter as Application["status"] | undefined,
        search: searchQuery || undefined,
      });

      if (result.success) {
        setApplications(result.applications || result.data || []);
        setPagination({
          total: result.pagination?.total || 0,
          pages: result.pagination?.pages || 1,
        });
      }
    } catch (err: any) {
      showError("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchApplications();
  };

  const handleReview = async (
    status: "approved" | "rejected" | "under_review" | "shortlisted",
  ) => {
    if (!selectedApp) return;

    setReviewing(true);
    try {
      await applicationApi.reviewApplication(selectedApp._id, {
        status,
        reviewNotes: reviewNotes || undefined,
        rejectionReason: status === "rejected" ? rejectionReason : undefined,
      });

      showSuccess(
        status === "approved"
          ? "Application approved!"
          : status === "rejected"
            ? "Application rejected"
            : "Application updated",
      );

      setSelectedApp(null);
      setReviewNotes("");
      setRejectionReason("");
      fetchApplications();
    } catch (err: any) {
      showError(err.message || "Failed to review application");
    } finally {
      setReviewing(false);
    }
  };

  const filteredApps = (applications || []).filter((app) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      app.headline?.toLowerCase().includes(query) ||
      app.bio?.toLowerCase().includes(query) ||
      app.user?.name?.toLowerCase().includes(query)
    );
  });

  const getStatusCount = (status: string) => {
    return (applications || []).filter((a) => a.status === status).length;
  };

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-amber-600 to-orange-500 shadow-sm">
              <Users className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-gray-900 md:text-xl">
              Applications
            </h1>
          </div>
          <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
            Review and manage mentor/instructor applications
          </p>
        </div>

        {/* Stats Cards - Responsive Grid */}
        <div className="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-4">
          {[
            {
              status: "pending",
              label: "Pending",
              icon: Clock,
              color: "bg-yellow-100",
              iconColor: "text-yellow-600",
            },
            {
              status: "under_review",
              label: "Under Review",
              icon: FileText,
              color: "bg-blue-100",
              iconColor: "text-blue-600",
            },
            {
              status: "approved",
              label: "Approved",
              icon: CheckCircle,
              color: "bg-green-100",
              iconColor: "text-green-600",
            },
            {
              status: "rejected",
              label: "Rejected",
              icon: XCircle,
              color: "bg-red-100",
              iconColor: "text-red-600",
            },
          ].map((item) => (
            <button
              key={item.status}
              onClick={() => {
                setStatusFilter(item.status);
                setCurrentPage(1);
              }}
              className={`bg-white rounded-xl p-3 shadow-sm border transition-all ${
                statusFilter === item.status
                  ? "border-orange-500 ring-2 ring-orange-200"
                  : "border-gray-100 hover:border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div
                  className={`w-7 h-7 rounded-lg flex items-center justify-center ${item.color}`}
                >
                  <item.icon className={`w-3.5 h-3.5 ${item.iconColor}`} />
                </div>
                <span className="text-sm font-bold text-gray-900">
                  {getStatusCount(item.status)}
                </span>
              </div>
              <p className="text-[9px] text-gray-500">{item.label}</p>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-3 mb-5 shadow-sm">
          <form onSubmit={handleSearch} className="space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, headline, or bio..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-[10px] focus:outline-none focus:border-orange-400"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-[10px] focus:outline-none focus:border-orange-400"
              >
                <option value="">All Types</option>
                <option value="mentor">Mentor</option>
                <option value="instructor">Instructor</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-[10px] focus:outline-none focus:border-orange-400"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="shortlisted">Shortlisted</option>
              </select>
              <button
                type="submit"
                className="px-3 py-1.5 bg-gradient-to-r from-orange-600 to-orange-500 text-white text-[10px] font-medium rounded-lg hover:shadow-md transition-all"
              >
                Search
              </button>
            </div>
            {(statusFilter || typeFilter || searchQuery) && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("");
                  setTypeFilter("");
                  setCurrentPage(1);
                }}
                className="text-[9px] text-orange-600 hover:text-orange-700"
              >
                Clear filters
              </button>
            )}
          </form>
        </div>

        {/* Applications List - Mobile Card View */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : filteredApps.length === 0 ? (
          <div className="py-12 text-center bg-white rounded-xl border border-gray-100">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-xs text-gray-500">No applications found</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="space-y-3 md:hidden">
              {filteredApps.map((app) => (
                <div
                  key={app._id}
                  className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {app.user?.avatar ? (
                        <img
                          src={app.user.avatar}
                          alt={app.user?.firstName  || "User"}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-500" />
                        </div>
                      )}
                      <div>
                        <p className="text-xs font-semibold text-gray-900">
                          {app.user?.firstName || "User"}
                        </p>
                        <p className="text-[9px] text-gray-500">
                          {app.user?.email}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full ${
                        app.applicationType === "mentor"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-purple-100 text-purple-700"
                      }`}
                    >
                      {app.applicationType}
                    </span>
                  </div>

                  <div className="mb-2">
                    <p className="text-[10px] font-medium text-gray-700 truncate">
                      {app.headline}
                    </p>
                    <p className="text-[9px] text-gray-500 mt-0.5 line-clamp-2">
                      {app.bio}
                    </p>
                  </div>

                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] text-gray-500">
                        {app.yearsOfExperience} years exp
                      </span>
                    </div>
                    <span
                      className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full ${
                        STATUS_COLORS[app.status] || "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {app.status.replace("_", " ")}
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <span className="text-[8px] text-gray-400">
                      {new Date(app.createdAt).toLocaleDateString()}
                    </span>
                    <button
                      onClick={() => setSelectedApp(app)}
                      className="text-[9px] text-orange-600 font-medium hover:underline"
                    >
                      Review →
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">
                        Applicant
                      </th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">
                        Type
                      </th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">
                        Headline
                      </th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">
                        Experience
                      </th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">
                        Status
                      </th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">
                        Date
                      </th>
                      <th className="text-right p-3 text-gray-600 font-medium text-[11px]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredApps.map((app) => (
                      <tr
                        key={app._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {app.user?.avatar ? (
                              <img
                                src={app.user.avatar}
                                alt={app.user?.name || "User"}
                                className="w-7 h-7 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center">
                                <User className="w-3.5 h-3.5 text-gray-500" />
                              </div>
                            )}
                            <div>
                              <p className="text-[11px] font-medium text-gray-900">
                                {app.user?.name || "User"}
                              </p>
                              <p className="text-[9px] text-gray-500 truncate max-w-[120px]">
                                {app.user?.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <span
                            className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${
                              app.applicationType === "mentor"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-purple-100 text-purple-700"
                            }`}
                          >
                            {app.applicationType}
                          </span>
                        </td>
                        <td className="p-3">
                          <p className="text-[11px] text-gray-900 max-w-[200px] truncate">
                            {app.headline}
                          </p>
                        </td>
                        <td className="p-3">
                          <span className="text-[11px] text-gray-600">
                            {app.yearsOfExperience} years
                          </span>
                        </td>
                        <td className="p-3">
                          <span
                            className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${
                              STATUS_COLORS[app.status] ||
                              "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {app.status.replace("_", " ")}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-[10px] text-gray-500">
                            {new Date(app.createdAt).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => setSelectedApp(app)}
                            className="text-orange-600 hover:text-orange-700 text-[11px] font-medium"
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex flex-col items-center gap-2 mt-5 sm:flex-row sm:justify-between">
            <p className="text-[9px] text-gray-500 order-2 sm:order-1">
              Showing {filteredApps.length} of {pagination.total} applications
            </p>
            <div className="flex items-center gap-1 order-1 sm:order-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-[10px] text-gray-600">
                Page {currentPage} of {pagination.pages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(pagination.pages, p + 1))
                }
                disabled={currentPage === pagination.pages}
                className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Review Modal - Mobile Responsive */}
      {selectedApp && (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center bg-black/50 backdrop-blur-sm sm:p-4">
          <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-gray-900">
                  Review Application
                </h2>
                <p className="text-[9px] text-gray-500">
                  Submitted on{" "}
                  {new Date(selectedApp.createdAt).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => setSelectedApp(null)}
                className="p-1.5 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {/* Applicant Info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  {selectedApp.user?.avatar ? (
                    <img
                      src={selectedApp.user.avatar}
                      alt={selectedApp.user?.name || "User"}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-600" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-900">
                      {selectedApp.user?.name || "User"}
                    </p>
                    <p className="text-[9px] text-gray-500">
                      {selectedApp.user?.email}
                    </p>
                  </div>
                  <span
                    className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full ${
                      selectedApp.applicationType === "mentor"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-purple-100 text-purple-700"
                    }`}
                  >
                    {selectedApp.applicationType}
                  </span>
                </div>

                <div className="space-y-1 text-[10px]">
                  {selectedApp.phone && (
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <Phone className="w-3 h-3" />
                      {selectedApp.phone}
                    </div>
                  )}
                  {selectedApp.linkedInUrl && (
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <Linkedin className="w-3 h-3" />
                      <a
                        href={selectedApp.linkedInUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-600 hover:underline"
                      >
                        LinkedIn Profile
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Application Details */}
              <div>
                <h3 className="text-[10px] font-medium text-gray-700 mb-1">
                  Headline
                </h3>
                <p className="text-[11px] text-gray-900">
                  {selectedApp.headline}
                </p>
              </div>

              <div>
                <h3 className="text-[10px] font-medium text-gray-700 mb-1">
                  Bio
                </h3>
                <p className="text-[10px] text-gray-600 leading-relaxed">
                  {selectedApp.bio}
                </p>
              </div>

              <div>
                <h3 className="text-[10px] font-medium text-gray-700 mb-1">
                  Areas of Expertise
                </h3>
                <div className="flex flex-wrap gap-1">
                  {selectedApp.expertise?.map((exp, i) => (
                    <span
                      key={i}
                      className="text-[8px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-full"
                    >
                      {exp}
                    </span>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <h3 className="text-[10px] font-medium text-gray-700 mb-1">
                    Years of Experience
                  </h3>
                  <p className="text-[11px] text-gray-600">
                    {selectedApp.yearsOfExperience} years
                  </p>
                </div>

                {selectedApp.mentoringTopics &&
                  selectedApp.mentoringTopics.length > 0 && (
                    <div>
                      <h3 className="text-[10px] font-medium text-gray-700 mb-1">
                        Mentoring Topics
                      </h3>
                      <div className="flex flex-wrap gap-1">
                        {selectedApp.mentoringTopics
                          .slice(0, 2)
                          .map((topic, i) => (
                            <span
                              key={i}
                              className="text-[8px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded"
                            >
                              {topic.length > 15
                                ? topic.substring(0, 12) + "..."
                                : topic}
                            </span>
                          ))}
                        {selectedApp.mentoringTopics.length > 2 && (
                          <span className="text-[8px] text-gray-500">
                            +{selectedApp.mentoringTopics.length - 2}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
              </div>

              {selectedApp.previousTeachingExperience && (
                <div>
                  <h3 className="text-[10px] font-medium text-gray-700 mb-1">
                    Previous Teaching Experience
                  </h3>
                  <p className="text-[10px] text-gray-600">
                    {selectedApp.previousTeachingExperience}
                  </p>
                </div>
              )}

              {selectedApp.notableAchievements && (
                <div>
                  <h3 className="text-[10px] font-medium text-gray-700 mb-1">
                    Notable Achievements
                  </h3>
                  <p className="text-[10px] text-gray-600">
                    {selectedApp.notableAchievements}
                  </p>
                </div>
              )}

              {/* Review Actions */}
              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="mb-3">
                  <label className="block text-[10px] font-medium text-gray-700 mb-1">
                    Review Notes{" "}
                    <span className="text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    rows={2}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-[10px] focus:outline-none focus:border-orange-400"
                    placeholder="Add notes about this application..."
                  />
                </div>

                {selectedApp.status === "pending" && (
                  <div className="mb-3">
                    <label className="block text-[10px] font-medium text-gray-700 mb-1">
                      Rejection Reason{" "}
                      <span className="text-gray-400">(if rejecting)</span>
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={2}
                      className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-[10px] focus:outline-none focus:border-orange-400"
                      placeholder="Reason for rejection..."
                    />
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleReview("under_review")}
                    disabled={
                      reviewing || selectedApp.status === "under_review"
                    }
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 text-white text-[9px] font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    <FileText className="w-3 h-3" />
                    Review
                  </button>
                  <button
                    onClick={() => handleReview("approved")}
                    disabled={reviewing || selectedApp.status === "approved"}
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-green-600 text-white text-[9px] font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <CheckCircle className="w-3 h-3" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleReview("rejected")}
                    disabled={reviewing || selectedApp.status === "rejected"}
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-red-600 text-white text-[9px] font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    <XCircle className="w-3 h-3" />
                    Reject
                  </button>
                  <button
                    onClick={() => handleReview("shortlisted")}
                    disabled={reviewing || selectedApp.status === "shortlisted"}
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-purple-600 text-white text-[9px] font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    <Star className="w-3 h-3" />
                    Shortlist
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminApplicationsPage;
